import type {
	ComponentPropsWithRef,
	HTMLAttributes,
	ReactNode,
	Ref,
	TdHTMLAttributes,
	ThHTMLAttributes,
} from "react";
import { createContext, isValidElement, useContext } from "react";
import type {
	CellProps as AriaCellProps,
	ColumnProps as AriaColumnProps,
	RowProps as AriaRowProps,
	TableHeaderProps as AriaTableHeaderProps,
	TableProps as AriaTableProps,
} from "react-aria-components";
import {
	Cell as AriaCell,
	Collection as AriaCollection,
	Column as AriaColumn,
	Row as AriaRow,
	Table as AriaTable,
	TableBody as AriaTableBody,
	TableHeader as AriaTableHeader,
	useTableOptions,
} from "react-aria-components";
import { Badge } from "@/components/base/badges/badges";
import { Checkbox } from "@/components/base/checkbox/checkbox";
import { cx } from "@/utils/cx";
import { TableBodyCell, TableBodyCellState } from "./table-body-cell";
import {
	type TableBodyRowState,
	tableBodyRowInteractiveClassName,
	tableBodyRowStateFromAria,
} from "./table-body-row";
import {
	TableHeaderCell,
	TableHeaderCellAlign,
	TableHeaderCellSortable,
	TableHeaderCellState,
} from "./table-header-cell";
import { tableHeaderRowAriaClassName } from "./table-header-row";
import { TableSelectionCellHeader } from "./table-selection-cell-header";
import { TableSelectionCellRow } from "./table-selection-cell-row";

// CustomTableCell.displayName = "CustomTableCell";

const TableContext = createContext<{ size: "sm" | "md" }>({ size: "md" });

const CustomTableCardRoot = ({
	children,
	className,
	size = "md",
	isLoading = false,
	...props
}: HTMLAttributes<HTMLDivElement> & {
	size?: "sm" | "md";
	isLoading?: boolean;
}) => {
	return (
		<TableContext.Provider value={{ size }}>
			<div
				{...props}
				className={cx(
					// Figma Table scroll wrapper (3101:15096) — radius-2xl (16px) +
					// primary border; overflow clips header/row fills to the curve
					"overflow-x-auto overflow-y-clip rounded-2xl bg-primary",
					!isLoading && "border border-primary shadow-xs",
					className,
				)}
			>
				{children}
			</div>
		</TableContext.Provider>
	);
};

interface CustomTableCardHeaderProps {
	/** The badge displayed next to the title. */
	badge?: ReactNode;
	/** The description of the table card header. */
	description?: string;
	/** The content displayed after the title and badge. */
	contentTrailing?: ReactNode;
	/** The class name of the table card header. */
	className?: string;
	/** Whether to show the padding on the table card header. */
	showPadding?: boolean;
}

const CustomTableCardHeader = ({
	badge,
	description,
	contentTrailing,
	className,
	showPadding = true,
}: CustomTableCardHeaderProps) => {
	const { size } = useContext(TableContext);

	return (
		<div
			className={cx(
				"relative flex flex-col items-start gap-4 border-secondary bg-primary px-4 md:flex-row",
				showPadding && "border-b",
				showPadding && (size === "sm" ? "py-4 md:px-5" : "py-5 md:px-6"),
				className,
			)}
		>
			<div className="flex flex-1 flex-col gap-0.5">
				<div className="flex items-center gap-2">
					{badge ? (
						isValidElement(badge) ? (
							badge
						) : (
							<Badge color="brand" size="sm">
								{badge}
							</Badge>
						)
					) : null}
				</div>
				{description && <p className="text-sm text-tertiary">{description}</p>}
			</div>
			{contentTrailing}
		</div>
	);
};

interface CustomTableRootProps
	extends AriaTableProps,
		Omit<ComponentPropsWithRef<"table">, "className" | "slot" | "style"> {
	size?: "sm" | "md";
	/** Disable the overflow-x-auto wrapper (useful for virtualized tables with external scroll containers) */
	disableOverflowWrapper?: boolean;
}

export const CustomTableRoot = ({
	className,
	size = "md",
	title,
	disableOverflowWrapper = false,
	...props
}: CustomTableRootProps) => {
	const context = useContext(TableContext);

	const table = (
		<AriaTable
			className={(state) =>
				cx(
					// min-w-full: fill the card when columns are narrow; w-max: grow
					// exactly to column content (no phantom trailing scroll width).
					"w-max min-w-full overflow-x-hidden",
					typeof className === "function" ? className(state) : className,
				)
			}
			aria-label={title ?? "Table"}
			{...props}
		/>
	);

	return (
		<TableContext.Provider value={{ size: context?.size ?? size }}>
			{disableOverflowWrapper ? table : <div>{table}</div>}
		</TableContext.Provider>
	);
};

// return (
// 	<TableContext.Provider value={{ size: context?.size ?? size }}>
// 		<AriaTable
// 			className={(state) =>
// 				cx(
// 					"w-full",
// 					typeof className === "function" ? className(state) : className,
// 				)
// 			}
// 			aria-label={title}
// 			{...props}
// 		/>
// 	</TableContext.Provider>
// );
// };
CustomTableRoot.displayName = "CustomTable";

interface CustomTableHeaderProps<T extends object>
	extends AriaTableHeaderProps<T>,
		Omit<
			ComponentPropsWithRef<"thead">,
			"children" | "className" | "slot" | "style"
		> {
	bordered?: boolean;
	/**
	 * Values that must refresh header cells when they change.
	 * Pass TanStack `sorting` so Destiny {@link TableHeaderCell} `sortable`
	 * (unsorted → asc → desc) is not stuck on a cached first render.
	 */
	dependencies?: readonly unknown[];
}

const CustomTableHeader = <T extends object>({
	columns,
	children,
	bordered = true,
	className,
	dependencies = [],
	...props
}: CustomTableHeaderProps<T>) => {
	const { size } = useContext(TableContext);
	const { selectionBehavior, selectionMode } = useTableOptions();

	return (
		<AriaTableHeader
			{...props}
			data-slot="table-header-row"
			className={(state) =>
				cx(
					// Destiny HeaderRow chrome (shared with TableHeaderRow)
					tableHeaderRowAriaClassName({
						bordered,
						selectable: selectionBehavior === "toggle",
					}),
					typeof className === "function" ? className(state) : className,
				)
			}
		>
			{selectionBehavior === "toggle" && (
				<AriaColumn className="relative w-[52px] bg-tertiary p-0">
					{selectionMode === "multiple" && (
						<TableSelectionCellHeader className="bg-tertiary">
							<Checkbox slot="selection" size={size} />
						</TableSelectionCellHeader>
					)}
				</AriaColumn>
			)}
			<AriaCollection items={columns} dependencies={dependencies}>
				{children}
			</AriaCollection>
		</AriaTableHeader>
	);
};

CustomTableHeader.displayName = "CustomTableHeader";

const contentAlignToHeaderAlign = {
	start: TableHeaderCellAlign.Left,
	center: TableHeaderCellAlign.Center,
	end: TableHeaderCellAlign.Right,
} as const;

interface CustomTableHeadProps
	extends AriaColumnProps,
		Omit<
			ThHTMLAttributes<HTMLTableCellElement>,
			"children" | "className" | "id"
		> {
	label?: string;
	/** Optional overline above the column label (Figma hasOverline). */
	overline?: string;
	/** Help-icon tooltip (Figma hasTooltip) — not the sort tip. */
	tooltip?: string;
	/**
	 * Optional sort tip for the whole header cell (TanStack next-sort copy).
	 * Distinct from {@link tooltip} which drives the Destiny help icon.
	 */
	sortTooltip?: string;
	style?: React.CSSProperties;
	resizer?: ReactNode;
	/** Horizontal alignment of header label/content within the column. */
	contentAlign?: "start" | "center" | "end";
	/**
	 * Destiny sort affordance. Prefer passing TanStack state via
	 * {@link tableHeaderCellSortableFromColumn}; falls back to Aria sortDirection.
	 */
	sortable?: TableHeaderCellSortable;
	/** Leading drag handle chrome (Figma hasDragHandle). */
	hasDragHandle?: boolean;
	/**
	 * Featured-icon gutter — symmetric 20px (px-5) padding, no text-column chrome.
	 * @see TableColumnMeta.isIconGutter
	 */
	isIconGutter?: boolean;
	/**
	 * Leading selection gutter — 32px / 4px padding, 52px wide.
	 * @see TableColumnMeta.isSelectionGutter
	 */
	isSelectionGutter?: boolean;
	/**
	 * Last leaf column — keep the resize handle inside the cell so `translateX(50%)`
	 * does not extend past the table and inflate horizontal scroll width.
	 */
	isLastColumn?: boolean;
}

const CustomTableHead = ({
	className,
	tooltip,
	sortTooltip,
	label,
	overline,
	children,
	style,
	resizer,
	contentAlign = "start",
	sortable: sortableProp,
	hasDragHandle = false,
	isIconGutter = false,
	isSelectionGutter = false,
	isLastColumn = false,
	...props
}: CustomTableHeadProps) => {
	return (
		<AriaColumn
			{...props}
			style={style}
			className={(state) =>
				cx(
					// Aria th is the layout shell; Destiny TableHeaderCell paints chrome.
					// Keep overflow visible so the absolute resize handle (translateX 50%)
					// stays hittable outside the cell edge (except last column).
					"group/header relative h-10 max-w-[215px] overflow-visible bg-tertiary p-0 outline-hidden transition-colors focus-visible:z-1 focus-visible:ring-2 focus-visible:ring-focus-ring focus-visible:ring-inset",
					(isIconGutter || isSelectionGutter) && "max-w-none",
					isLastColumn && "overflow-hidden",
					sortableProp != null &&
						sortableProp !== TableHeaderCellSortable.None &&
						"cursor-pointer",
					typeof className === "function" ? className(state) : className,
				)
			}
		>
			{(state) => {
				// Destiny HeaderCell `sortable`: none | unsorted | asc | desc
				// Live tables pass TanStack state via `sortable` prop.
				const sortable = sortableProp ?? TableHeaderCellSortable.None;

				const headerState = state.isFocused
					? TableHeaderCellState.Focused
					: state.isHovered
						? TableHeaderCellState.Hover
						: TableHeaderCellState.Default;

				const headerContent =
					typeof children === "function" ? children(state) : children;

				return (
					<>
						{isSelectionGutter ? (
							<TableSelectionCellHeader>
								{headerContent}
							</TableSelectionCellHeader>
						) : (
							<TableHeaderCell
								align={contentAlignToHeaderAlign[contentAlign]}
								sortable={sortable}
								state={headerState}
								label={label}
								overline={overline}
								hasOverline={Boolean(overline)}
								hasTooltip={Boolean(tooltip)}
								tooltip={tooltip}
								hasDragHandle={hasDragHandle}
								title={sortTooltip}
								className={cx(
									"h-full min-w-0 w-auto max-w-none group-hover/header:bg-quaternary",
									// Match BodyCell px-5 (20px) — default HeaderCell is px-6
									isIconGutter && "px-5",
								)}
							>
								{headerContent}
							</TableHeaderCell>
						)}
						{/* Sibling of HeaderCell so overflow/clip on the label never eats the handle */}
						{resizer}
					</>
				);
			}}
		</AriaColumn>
	);
};
CustomTableHead.displayName = "CustomTableHead";

interface CustomTableRowProps<T extends object>
	extends AriaRowProps<T>,
		Omit<
			ComponentPropsWithRef<"tr">,
			"children" | "className" | "onClick" | "slot" | "style" | "id"
		> {
	highlightSelectedRow?: boolean;
}

const CustomTableRow = <T extends object>({
	columns,
	children,
	className,
	highlightSelectedRow = true,
	...props
}: CustomTableRowProps<T>) => {
	const { size } = useContext(TableContext);
	const { selectionBehavior } = useTableOptions();

	return (
		<AriaRow
			{...props}
			data-slot="table-body-row"
			className={(state) =>
				cx(
					// Destiny BodyRow chrome (shared with TableBodyRow)
					tableBodyRowInteractiveClassName,
					highlightSelectedRow &&
						"selected:bg-[rgba(181,71,8,0.1)] selected:[&>td]:bg-[rgba(181,71,8,0.1)] selected:[&>th]:bg-[rgba(181,71,8,0.1)]",
					// Trailing edge: BodyCell px-5 (20px) — no extra last-column pr-8
					// (that left an empty strip past content and inflated scroll width).
					typeof className === "function" ? className(state) : className,
				)
			}
			// Reflect Destiny TableBodyRowState for CSS/tooling (alongside RAC data-selected)
			style={(state) => {
				const rowState: TableBodyRowState = tableBodyRowStateFromAria(state);
				const prev = props.style;
				const base =
					typeof prev === "function" ? prev(state) : (prev ?? undefined);
				return {
					...(typeof base === "object" && base !== null ? base : {}),
					["--destiny-table-body-row-state" as string]: rowState,
				};
			}}
		>
			{selectionBehavior === "toggle" && (
				<AriaCell className="relative w-[52px] p-0">
					<TableSelectionCellRow>
						<Checkbox slot="selection" size={size} />
					</TableSelectionCellRow>
				</AriaCell>
			)}
			<AriaCollection items={columns}>{children}</AriaCollection>
		</AriaRow>
	);
};

CustomTableRow.displayName = "CustomTableRow";

interface CustomTableCellProps
	extends AriaCellProps,
		Omit<
			TdHTMLAttributes<HTMLTableCellElement>,
			"children" | "className" | "id"
		> {
	ref?: Ref<HTMLTableCellElement>;
	style?: React.CSSProperties;
	/**
	 * Featured-icon gutter — keep symmetric px-5 (20px); skip text-column max-width.
	 * @see TableColumnMeta.isIconGutter
	 */
	isIconGutter?: boolean;
	/**
	 * Leading selection gutter — SelectionCellRow chrome instead of BodyCell.
	 * @see TableColumnMeta.isSelectionGutter
	 */
	isSelectionGutter?: boolean;
	/**
	 * When true, cell grows with content (min 50px) instead of fixed 50px height.
	 * Opt-in only — used by tables with multi-line stacked cell content.
	 */
	allowAutoHeight?: boolean;
}

const CustomTableCell = ({
	className,
	children,
	style,
	isIconGutter = false,
	isSelectionGutter = false,
	allowAutoHeight = false,
	...props
}: CustomTableCellProps) => {
	return (
		<AriaCell
			{...props}
			style={style}
			className={(state) =>
				cx(
					// Aria td is the layout shell; Destiny TableBodyCell paints chrome.
					allowAutoHeight
						? "relative h-auto! max-h-none! min-h-[50px] max-w-[215px] overflow-visible p-0 outline-focus-ring focus-visible:z-1 focus-visible:outline-2 focus-visible:-outline-offset-2"
						: "relative h-[50px] max-h-[50px] max-w-[215px] overflow-visible p-0 outline-focus-ring focus-visible:z-1 focus-visible:outline-2 focus-visible:-outline-offset-2",
					(isIconGutter || isSelectionGutter) && "max-w-none",
					typeof className === "function" ? className(state) : className,
				)
			}
		>
			{(values) => {
				const cellContent =
					typeof children === "function" ? children(values) : children;

				if (isSelectionGutter) {
					return (
						<TableSelectionCellRow
							className={
								allowAutoHeight
									? "h-auto! max-h-none! min-h-[50px]"
									: undefined
							}
						>
							{cellContent}
						</TableSelectionCellRow>
					);
				}

				return (
					// Hover / selected / focus chrome lives on the Aria row (and td
					// focus ring) — keep TableBodyCell at default so cell fills do
					// not stack with BodyRow hover/selected.
					// Icon gutter: default BodyCell px-5 (20px L/R) — do not override pl.
					<TableBodyCell
						state={TableBodyCellState.Default}
						className={cx(
							"min-w-0 w-auto max-w-none",
							allowAutoHeight
								? "h-auto! max-h-none! min-h-[50px] items-start py-2"
								: "h-full",
						)}
					>
						{cellContent}
					</TableBodyCell>
				);
			}}
		</AriaCell>
	);
};
CustomTableCell.displayName = "CustomTableCell";

const CustomTableCard = {
	Root: CustomTableCardRoot,
	Header: CustomTableCardHeader,
};

const CustomTable = CustomTableRoot as typeof CustomTableRoot & {
	Body: typeof AriaTableBody;
	Cell: typeof CustomTableCell;
	Column: typeof AriaColumn;
	Head: typeof CustomTableHead;
	Header: typeof CustomTableHeader;
	Row: typeof CustomTableRow;
};
CustomTable.Column = AriaColumn;
CustomTable.Body = AriaTableBody;
CustomTable.Cell = CustomTableCell;
CustomTable.Head = CustomTableHead;
CustomTable.Header = CustomTableHeader;
CustomTable.Row = CustomTableRow;

export { CustomTable, CustomTableCard };
